import { Body, Button, ButtonGroup, Tag, TinyTitle, useFlashMessage } from '@indigo-ag/component-module';
import {
  BackgroundCheckStep,
  BackgroundCheckStepStatus,
  BackgroundCheckSupportingDocumentType
} from '@indigo-ag/schema';
import i18next from 'core/i18n';
import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { useMutation } from '@apollo/client';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import {
  getBackgroundCheckStepEvents,
  getOpportunityContactRolesAndBackgroundCheckGroup,
  retryBackgroundCheckStep
} from 'features/brazil-grower/queries/backgroundCheck.queries';
import { Card } from 'shared';
import {
  getCheckStepStatusVariant,
  getStepStatusTranslation,
  getStepTypeTranslation
} from '../../utils/backgroundCheck.util';
import { BackgroundCheckFile } from './BackgroundCheckFile';
import { BackgroundCheckStepEventsModal } from './BackgroundCheckStepEventsModal';
import { EditBackgroundCheckStepModal } from './EditBackgroundCheckStepModal';

const CardContainer = styled.div`
  height: 100%;
  flex: 1;
  min-width: 300px;
`;

const CardContentContainer = styled.div`
  min-height: 50px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const getFileName = (accessibleUrl: string | undefined, documentType: BackgroundCheckSupportingDocumentType) => {
  if (!accessibleUrl) {
    return documentType;
  }

  return new URL(accessibleUrl).pathname.slice(1);
};

const isStepStatusEditable = (status: BackgroundCheckStepStatus) =>
  [
    BackgroundCheckStepStatus.FAILED_TENTATIVE,
    BackgroundCheckStepStatus.PASSED_TENTATIVE,
    BackgroundCheckStepStatus.FAILED,
    BackgroundCheckStepStatus.PASSED,
    BackgroundCheckStepStatus.SYSTEM_ERROR
  ].includes(status);

const isStepStatusRetryable = (status: BackgroundCheckStepStatus) => status === BackgroundCheckStepStatus.SYSTEM_ERROR;

interface BackgroundCheckStepCardProps {
  backgroundCheckStep: BackgroundCheckStep;
  readOnly?: boolean;
}

export const BackgroundCheckStepCard: React.FunctionComponent<BackgroundCheckStepCardProps> = ({
  backgroundCheckStep: { id: stepId, supportingDocument, status, type: stepType },
  readOnly
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEventsModalOpen, setIsEventsModalOpen] = useState(false);
  const isRetryable = !readOnly && isStepStatusRetryable(status);
  const isEditable = !readOnly && isStepStatusEditable(status);
  const { orderInfo } = useContext(FinanceProcessContext);
  const opportunityId = orderInfo?.opportunities?.[0].id;

  const [retryStep] = useMutation(retryBackgroundCheckStep, {
    variables: { stepId },
    refetchQueries: [
      { query: getOpportunityContactRolesAndBackgroundCheckGroup, variables: { opportunityId } },
      { query: getBackgroundCheckStepEvents, variables: { stepId, limit: 5, offset: 0 } }
    ]
  });
  const { addFlashMessage } = useFlashMessage();

  const onClickRetry = async () => {
    const { errors } = await retryStep({ variables: { stepId } });

    if (errors?.length) {
      addFlashMessage({
        content: i18next.t('backgroundCheckStep_RetryFlashMessage_error'),
        title: '',
        variant: 'error'
      });
    } else {
      addFlashMessage({
        content: i18next.t('backgroundCheckStep_RetryFlashMessage_success'),
        title: '',
        variant: 'success'
      });
    }
  };

  return (
    <>
      <EditBackgroundCheckStepModal
        isOpen={isEditModalOpen}
        onRequestClose={() => setIsEditModalOpen(false)}
        stepId={stepId}
        stepStatus={status}
      />
      <BackgroundCheckStepEventsModal
        isOpen={isEventsModalOpen}
        onRequestClose={() => setIsEventsModalOpen(false)}
        stepId={stepId}
        stepType={stepType}
      />
      <CardContainer>
        <Card
          title={<TinyTitle>{getStepTypeTranslation(stepType)}</TinyTitle>}
          topRightContent={
            <Tag label={getStepStatusTranslation(status)} variant={getCheckStepStatusVariant(status)} hideIcon />
          }
        >
          {status !== BackgroundCheckStepStatus.NOT_STARTED && (
            <CardContentContainer>
              {status === BackgroundCheckStepStatus.PROCESSING && (
                <Body>{i18next.t('backgroundCheckStepStatus_InProgressContent')}</Body>
              )}
              {status === BackgroundCheckStepStatus.SYSTEM_ERROR && (
                <Body>{i18next.t('backgroundCheckStepStatus_SystemErrorContent')}</Body>
              )}
              {supportingDocument ? (
                <BackgroundCheckFile
                  accessibleUrl={supportingDocument.accessibleUrl}
                  createdAt={supportingDocument.createdAt}
                  fileName={getFileName(supportingDocument.accessibleUrl, supportingDocument.type)}
                />
              ) : null}
            </CardContentContainer>
          )}

          <ButtonsWrapper>
            <ButtonGroup>
              {(isEditable || isRetryable) && (
                <>
                  <Button
                    disabled={!isEditable}
                    onClick={() => setIsEditModalOpen(true)}
                    size="small"
                    style={{ visibility: isEditable ? 'visible' : 'hidden' }}
                    variant="secondary"
                  >
                    {i18next.t('edit')}
                  </Button>
                  <Button
                    disabled={!isRetryable}
                    onClick={onClickRetry}
                    style={{ visibility: isRetryable ? 'visible' : 'hidden' }}
                    size="small"
                    variant="secondary"
                  >
                    {i18next.t('tryAgain')}
                  </Button>
                </>
              )}
            </ButtonGroup>
            <ButtonGroup>
              <Button onClick={() => setIsEventsModalOpen(true)} size="small" variant="tertiary">
                {i18next.t('detailButtonLabel')}
              </Button>
            </ButtonGroup>
          </ButtonsWrapper>
        </Card>
      </CardContainer>
    </>
  );
};
